import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import type {
  CompanyMatchResult,
  CompanyMatchResultApiResponse,
  CultureAgentResultsConfig,
  CultureAgentResultsError,
  SurveyData,
} from '../types';

import {
  createCultureAgentCoreRequest,
  cultureAgentCorePaths,
  obtainCultureAgentSession,
  refreshCultureAgentSessionOrThrow,
} from '../../culture-agent/api';

const toError = (code: string, message: string): CultureAgentResultsError => ({code, message});

const MATCH_REQUEST_LIMIT = process.env.NODE_ENV === 'development' ? Number.POSITIVE_INFINITY : 3;

export const useCultureAgentResults = (config: CultureAgentResultsConfig) => {
  const {apiHost, sessionId} = config;

  const [surveyData, setSurveyData] = useState<SurveyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<CultureAgentResultsError | null>(null);
  const [matchRequestCount, setMatchRequestCount] = useState<number | null>(null);

  const hasInitialized = useRef(false);

  const obtainSession = useCallback(async (): Promise<void> => {
    await obtainCultureAgentSession(apiHost, sessionId);
  }, [apiHost, sessionId]);

  const refreshSession = useCallback(() => refreshCultureAgentSessionOrThrow(apiHost), [apiHost]);

  const coreRequest = useMemo(() => createCultureAgentCoreRequest(apiHost, refreshSession), [apiHost, refreshSession]);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const init = async () => {
      if (!sessionId) {
        await obtainSession();
      }
      const response = await coreRequest(cultureAgentCorePaths.getResults, {method: 'GET'});
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw toError(
          body.error ?? 'FETCH_ERROR',
          body.message ?? `Failed to fetch survey data: ${response.statusText}`
        );
      }

      const data: SurveyData = await response.json();
      setSurveyData(data);
    };

    init()
      .catch((err) => setError(err.code ? err : toError('INIT_ERROR', err.message ?? 'Unknown error')))
      .finally(() => setLoading(false));
  }, [obtainSession, coreRequest, sessionId]);

  const getCompanyMatch = useCallback(
    async (companyName: string): Promise<{match: CompanyMatchResult}> => {
      const response = await coreRequest(cultureAgentCorePaths.companyMatchResult, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({companyName}),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const message = body.message ?? 'Failed to fetch company match result';
        if (response.status === 400) {
          throw toError(body.error ?? 'VALIDATION_ERROR', message);
        }
        if (response.status === 404) {
          throw toError(body.error ?? 'CULTURE_TYPE_NOT_FOUND', message);
        }
        if (response.status === 502) {
          throw toError(body.error ?? 'MATCH_SERVICE_ERROR', message);
        }
        throw toError(body.error ?? 'MATCH_ERROR', message);
      }

      const data: CompanyMatchResultApiResponse = await response.json();
      const countHeader = response.headers.get('X-Request-Count');
      const count = countHeader !== null ? parseInt(countHeader, 10) : 1;
      if (!Number.isNaN(count)) {
        setMatchRequestCount(count);
      }

      const match: CompanyMatchResult = {
        culturalMapping: data.culturalMapping,
        matchPercentage: data.matchPercentage,
        companyName: data.companyName,
        companyCultureType: data.companyCultureType,
        maxCompanyCultureType: data.maxCompanyCultureType,
        company_culture_type_info: data.company_culture_type_info ?? null,
        talent_culture_type_info: data.talent_culture_type_info ?? null,
      };
      return {match};
    },
    [coreRequest]
  );

  const isMatchLimitReached = matchRequestCount !== null && matchRequestCount >= MATCH_REQUEST_LIMIT;

  return {surveyData, loading, error, getCompanyMatch, isMatchLimitReached};
};
